<script>
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid"
import {
  CursorArrowRaysIcon,
  PhoneIcon,
  PlayIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  PresentationChartLineIcon,
} from "@heroicons/vue/24/outline"
import VideoDialog from "@/js/components/dialogs/VideoDialog.vue"
import { ref } from "vue"

export default {
  name: "Header",
  components: {
    VideoDialog,
    ChevronUpIcon,
    ChevronDownIcon,
  },
  props: {
    dark: Boolean,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const dialogs = ref({
      video: { toggle: false },
    })
    return {
      dialogs,
      route,
    }
  },
  data: function () {
    return {
      mobileNavOpen: false,
      top: true,
      showDropdown: true,
      showInstallers: false,

      solutions: [
        {
          name: "Plateforme de gestion de chantiers",
          description: "Simplifiez la gestion de vos projets solaires.",
          to: { name: "InstallerHome" },
          icon: Squares2X2Icon,
        },
        {
          name: "Assistance de maîtrise d'ouvrage",
          description: "Recevez des chantiers clés en main.",
          to: { name: "InstallerFindNewClients" },
          icon: CursorArrowRaysIcon,
        },
        {
          name: "Gestion administrative",
          description: "Automatisez vos démarches administratives.",
          to: { name: "InstallerAdministration" },
          icon: ShieldCheckIcon,
        },
        {
          name: "Études photovoltaïques",
          description: "Propositions en ligne accessibles via mini-site.",
          to: { name: "InstallerDigitalSalesTools" },
          icon: PresentationChartLineIcon,
        },
      ],
      callsToAction: [
        {
          name: "Vidéo",
          icon: PlayIcon,
        },
        {
          name: "+33 7 44 09 61 13",
          to: { name: "SupportSchedule", params: { slug: "installer" } },
          icon: PhoneIcon,
        },
      ],
    }
  },
  mounted() {
    document.addEventListener("click", this.clickOutside)
    document.addEventListener("keydown", this.keyPress)
    document.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    document.removeEventListener("click", this.clickOutside)
    document.removeEventListener("keydown", this.keyPress)
    document.removeEventListener("scroll", this.handleScroll)
  },
  methods: {
    clickOutside(e) {
      if (
        !this.mobileNavOpen ||
        this.$refs.mobileNav.contains(e.target) ||
        this.$refs.hamburger.contains(e.target)
      )
        return
      this.mobileNavOpen = false
    },
    keyPress() {
      if (!this.mobileNavOpen || event.keyCode !== 27) return
      this.mobileNavOpen = false
    },
    handleScroll() {
      this.top = window.pageYOffset > 10 ? false : true
    },
  },
}
</script>

<template>
  <header
    class="fixed w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out"
    :class="{ 'bg-white backdrop-blur-sm shadow-lg': !top }"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6">
      <div class="flex items-center justify-between h-14 md:h-20">
        <!-- Site branding -->
        <div class="shrink-0 mr-4">
          <!-- Logo -->
          <router-link
            to="/"
            class="flex gap-2 grow justify-end flex-wrap items-center"
            aria-label="projetsolaire"
          >
            <img
              class="w-10 h-10 md:w-12 md:h-12"
              src="https://images.ctfassets.net/1b6f0wuf6ave/42P3RP32nltWjyhI0fg0PY/57029d3dbc531bad21cd75c871078436/logo_official_1.svg"
            />
            <h2
              :class="dark && top ? 'text-gray-200' : 'text-gray-800'"
              class="hidden md:block h2 text-xl"
            >
              projetsolaire
            </h2>
          </router-link>
        </div>

        <!-- Desktop navigation -->
        <nav class="hidden md:flex md:grow">
          <!-- Desktop sign in links -->
          <ul class="flex grow justify-end flex-wrap items-center">
            <li>
              <router-link
                to="/blog"
                :class="
                  dark && top
                    ? 'text-gray-200 hover:text-gray-400'
                    : 'text-gray-600 hover:text-gray-900'
                "
                class="px-3 lg:px-5 py-2 flex items-center transition duration-150 ease-in-out"
                >{{ $t("navigation.blog") }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/about"
                :class="
                  dark && top
                    ? 'text-gray-200 hover:text-gray-400'
                    : 'text-gray-600 hover:text-gray-900'
                "
                class="px-3 lg:px-5 py-2 flex items-center transition duration-150 ease-in-out"
                >{{ $t("navigation.about-us") }}</router-link
              >
            </li>
            <template v-if="!route.path.startsWith('/ambassadors')">
              <li>
                <router-link
                  to="/signin"
                  :class="
                    dark && top
                      ? 'text-gray-200 hover:text-gray-400'
                      : 'text-gray-600 hover:text-gray-900'
                  "
                  class="font-medium px-5 py-3 flex items-center transition duration-150 ease-in-out"
                  >{{ $t("navigation.sign-in") }}</router-link
                >
              </li>
              <li>
                <router-link
                  to="/installer"
                  class="btn-sm text-gray-200 rounded-full bg-gray-900 hover:bg-gray-800 ml-3"
                >
                  <span>{{ $t("navigation.installer") }}</span>
                  <svg
                    class="w-3 h-3 fill-current text-gray-400 shrink-0 ml-2 -mr-1"
                    viewBox="0 0 12 12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.707 5.293L7 .586 5.586 2l3 3H0v2h8.586l-3 3L7 11.414l4.707-4.707a1 1 0 000-1.414z"
                      fill-rule="nonzero"
                    />
                  </svg>
                </router-link>
              </li>
            </template>
          </ul>
        </nav>

        <!-- Mobile menu -->
        <div class="flex md:hidden">
          <!-- Hamburger button -->
          <button
            ref="hamburger"
            class="hamburger"
            :class="{ active: mobileNavOpen }"
            aria-controls="mobile-nav"
            :aria-expanded="mobileNavOpen"
            @click="mobileNavOpen = !mobileNavOpen"
          >
            <span class="sr-only">Menu</span>
            <svg
              class="w-6 h-6 fill-current text-gray-900"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="4" width="24" height="2" />
              <rect y="11" width="24" height="2" />
              <rect y="18" width="24" height="2" />
            </svg>
          </button>

          <!-- Mobile navigation -->
          <transition
            enter-active-class="transition ease-out duration-200 transform"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-out duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <nav
              v-show="mobileNavOpen"
              id="mobile-nav"
              ref="mobileNav"
              class="absolute top-full h-screen pb-16 z-20 left-0 w-full overflow-scroll bg-white"
            >
              <ul class="px-5 py-8">
                <!-- Dropdown -->
                <li class="relative">
                  <!-- this button must take all the width with flex but don't-->
                  <button
                    class="flex cursor-pointer text-gray-600 hover:text-gray-900 py-2"
                    @click="showInstallers = !showInstallers"
                  >
                    {{ $t("navigation.installers") }}
                    <span class="ml-2">
                      <template v-if="showInstallers">
                        <ChevronUpIcon class="h-5" />
                      </template>
                      <template v-else>
                        <ChevronDownIcon class="h-5" />
                      </template>
                    </span>
                  </button>
                  <div v-if="showInstallers" class="mt-3 pb-3">
                    <a
                      v-for="item in solutions"
                      :key="item.name"
                      class="-m-3 flex items-start rounded-lg p-3 transition duration-150 ease-in-out hover:bg-gray-50 py-4"
                    >
                      <router-link :to="item.to">
                        <div class="ml-4">
                          <p class="text-gray-600">
                            {{ item.name }}
                          </p>
                        </div>
                      </router-link>
                    </a>
                  </div>
                </li>
                <!-- End of Dropdown -->

                <li>
                  <router-link
                    to="/blog"
                    class="flex text-gray-600 hover:text-gray-900 py-2"
                    >{{ $t("navigation.blog") }}</router-link
                  >
                </li>
                <li>
                  <router-link
                    to="/help"
                    class="flex text-gray-600 hover:text-gray-900 py-2"
                    >{{ $t("navigation.documentation") }}</router-link
                  >
                </li>
                <li>
                  <router-link
                    to="/about"
                    class="flex text-gray-600 hover:text-gray-900 py-2"
                    >{{ $t("navigation.about-us") }}</router-link
                  >
                </li>
                <template v-if="!route.path.startsWith('/ambassadors')">
                  <li>
                    <router-link
                      to="/signup"
                      class="flex text-gray-600 hover:text-gray-900 py-2"
                      >{{ $t("navigation.sign-up") }}</router-link
                    >
                  </li>

                  <li>
                    <router-link
                      to="/signin"
                      class="btn-sm text-gray-200 bg-gray-900 hover:bg-gray-800 w-full my-2"
                    >
                      <span>{{ $t("navigation.sign-in") }}</span>
                      <svg
                        class="w-3 h-3 fill-current text-gray-400 shrink-0 ml-2 -mr-1"
                        viewBox="0 0 12 12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.707 5.293L7 .586 5.586 2l3 3H0v2h8.586l-3 3L7 11.414l4.707-4.707a1 1 0 000-1.414z"
                          fill="#999"
                          fill-rule="nonzero"
                        />
                      </svg>
                    </router-link>
                  </li>
                </template>
              </ul>
            </nav>
          </transition>
        </div>
      </div>
    </div>
    <VideoDialog v-if="dialogs.video.toggle" v-model="dialogs.video.toggle" />
  </header>
</template>
